import {noop, sleep} from '@benchling/js-utils';
import {createStyles} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import * as AuditActions from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import {MoveToTemplateCollectionModal} from 'notebook/entry-templates/collections/MoveToTemplateCollectionModal';
import ProcedureErrorToolTip from 'procedures/design/error-panel/ProcedureErrorToolTip';
import DuplicateProcedureModal, {
  DuplicateProcedureModalProps,
} from 'procedures/design/modals/DuplicateProcedureModal';
import {ProcedureHistoryPanelButton} from 'procedures/design/ProcedureHistoryPanel';
import {
  ProcedurePublishingRecordsForButton,
  ProcedurePublishingRecordsForButtonVariables,
} from 'procedures/graphql/__generated/ProcedurePublishingRecordsForButton';
import {PROCEDURE_PUBLISHING_RECORDS_FOR_BUTTON} from 'procedures/graphql/ProceduresQueries';
import {
  createNewDraftProcedureVersion,
  publishProcedureVersion,
  withdrawProcedureVersion,
} from 'procedures/network/ProcedureDAO';
import {procedurePageUrl} from 'procedures/network/procedureUrls';
import {ProcedureMetadata} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import AutosaveManager from 'procedures/redux/autosaveManager';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {StaticTemplateFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {getStore} from 'redux-stores/Store';
import {getStudyTypeForProcedureType} from 'studies/TypedStudyUtils';
import {PublishingRecordForButton} from 'template-collectibles/publishing-ui/graphql/types';
import TemplatePublishingButton from 'template-collectibles/publishing-ui/TemplatePublishingButton';
import {getPublishableInfoForPublishingButton} from 'template-collectibles/publishing-ui/TemplatePublishingUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonSize} from 'ui-library/buttons/Button';
import {GRAY, hexToRgba, WHITE} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import OverflowMenu, {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import bufferForUX from 'util/bufferForUX';
import {withLoadingScreen} from 'util/Promises';

import NewStudyDropdown from '../../studies/sidepanel/NewStudyDropdown';
import useProcedureActions from './useProcedureActions';
import {ProcedureAccessLevel} from './validation/determineProcedureAccessLevel';
import {ProcedureValidationContext} from './validation/ProcedureValidationContext';

const useStyles = createStyles(() => ({
  button: {
    marginLeft: 5,
    marginRight: 5,
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 4,
    marginBottom: 10,
    marginRight: 15,
    whiteSpace: 'nowrap',
  },
  disablePlanExperimentPopover: {
    borderRadius: 3,
    background: WHITE,
    boxShadow: `0px 2px 8px 2px ${hexToRgba(GRAY, 0.8)};`,
    width: '170px',
    textAlign: 'center',
    lineHeight: '14px',
    margin: '-2px 0px',
  },
  publishingButton: {
    marginRight: 5,
    marginTop: 2,
  },
  rightGroup: {
    display: 'flex',
    marginTop: '-6px',
  },
}));

interface MenuItemFactoryParams {
  isMovable: boolean | null;
  isArchivable: boolean | null;
  isUnarchivable: boolean | null;
  locked: boolean;
  isProcedureArchived: boolean;
  isViewingLatestVersion: boolean;
  procedureMetadata: ProcedureMetadata;
  hasUnsavedChanges: boolean;
  onDuplicate: (params: {
    name: string;
    templateCollectionId: string;
    procedureType: ProcedureType;
  }) => Promise<void>;
  onMove: (templateCollectionId: string) => void;
  onArchive: (itemIds: Array<string>, purpose: string, options: ArchiveModal.ArchiveOptionsForModal) => void;
  onUnarchive: () => void;
}
const overflowMenuItemFactory = (factoryParams: MenuItemFactoryParams): Array<OverflowMenuOption> => {
  const moveMenuOption: Array<OverflowMenuOption> =
    !factoryParams.locked &&
    isFeatureEnabledForProcedureType(factoryParams.procedureMetadata.procedureType, ProceduresFeature.MOVE) &&
    factoryParams.isMovable
      ? [
          {
            icon: 'move',
            contents: <span>Move</span>,
            onSelect: () => {
              runModal(MoveToTemplateCollectionModal, {
                title: `Move ${getProcedureUIString(
                  ProceduresStringKeys.PROCEDURE,
                  factoryParams.procedureMetadata.procedureType,
                  {stringCase: StringCase.LOWER}
                )}`,
                onRequestSubmit: factoryParams.onMove,
                onRequestClose: noop,
                initialTemplateCollectionId: factoryParams.procedureMetadata.templateCollection.id,
                allowedOwnerIds:
                  // If the procedure is already in a org-owned template collection, it must stay in the same org.
                  factoryParams.procedureMetadata.templateCollection.owner.type ===
                  ModelTypeConstants.ORGANIZATION
                    ? [factoryParams.procedureMetadata.templateCollection.owner.id]
                    : undefined,
              });
            },
            isDisabled: factoryParams.hasUnsavedChanges,
          },
        ]
      : [];

  const auditMenuOption: Array<OverflowMenuOption> = isFeatureEnabledForProcedureType(
    factoryParams.procedureMetadata.procedureType,
    ProceduresFeature.EXPORT
  )
    ? [
        {
          icon: 'audit-trail',
          contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
          onSelect: () => {
            AuditActions.exportAuditLogs([factoryParams.procedureMetadata.id]);
          },
          isDisabled: factoryParams.hasUnsavedChanges || !factoryParams.isViewingLatestVersion,
        },
      ]
    : [];

  const duplicateMenuOption: Array<OverflowMenuOption> = isFeatureEnabledForProcedureType(
    factoryParams.procedureMetadata.procedureType,
    ProceduresFeature.CREATE
  )
    ? [
        {
          icon: 'copy',
          contents: <span>Duplicate</span>,
          onSelect: () => {
            runModal<DuplicateProcedureModalProps>(DuplicateProcedureModal, {
              sourceProcedureName: factoryParams.procedureMetadata.name,
              sourceProcedureTemplateCollectionId: factoryParams.procedureMetadata.templateCollection.id,
              sourceProcedureType: factoryParams.procedureMetadata.procedureType,
              onRequestClose: noop,
              onRequestSubmit: factoryParams.onDuplicate,
            });
          },
          isDisabled: factoryParams.hasUnsavedChanges || !factoryParams.isViewingLatestVersion,
        },
      ]
    : [];

  const archiveMenuOption = ((): Array<OverflowMenuOption> => {
    if (
      !isFeatureEnabledForProcedureType(
        factoryParams.procedureMetadata.procedureType,
        ProceduresFeature.ARCHIVE
      )
    ) {
      return [];
    } else {
      if (factoryParams.isArchivable && !factoryParams.isProcedureArchived) {
        return [
          {
            icon: 'archive',
            contents: <span>Archive</span>,
            onSelect: () => {
              ArchiveModal.open({
                items: [factoryParams.procedureMetadata],
                onArchive: factoryParams.onArchive,
              });
            },
            isDisabled: factoryParams.hasUnsavedChanges,
          },
        ];
      }

      if (
        factoryParams.isUnarchivable &&
        factoryParams.isProcedureArchived &&
        isFeatureEnabledForProcedureType(
          factoryParams.procedureMetadata.procedureType,
          ProceduresFeature.UNARCHIVE
        )
      ) {
        return [
          {
            icon: 'unarchive',
            contents: <span>Unarchive</span>,
            onSelect: () => {
              factoryParams.onUnarchive();
            },
            isDisabled: factoryParams.hasUnsavedChanges,
          },
        ];
      }
    }

    return [];
  })();
  return [...duplicateMenuOption, ...moveMenuOption, ...archiveMenuOption, ...auditMenuOption];
};

function useReasonPlanExperimentIsBlocked(
  numErrors: number,
  isAutosaverPaused: boolean,
  accessLevel: ProcedureAccessLevel | null,
  procedureType: ProcedureType,
  isViewingEffectiveVersion: boolean
): string | null {
  return useMemo<string | null>(() => {
    const procedureTypeNameSingular = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
      stringCase: StringCase.LOWER,
    });

    if (!isFeatureEnabledForProcedureType(procedureType, ProceduresFeature.PLAN_EXPERIMENT)) {
      const procedureTypeName = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
        stringCase: StringCase.SENTENCE,
        plural: true,
      });
      const studyType =
        procedureType === ProcedureType.RECIPE ? 'a process development' : 'an assay development';
      return `You do not have sufficient permissions to use ${procedureTypeName}, which is required to create ${studyType} study. Please contact your administrator.`;
    }

    if (!isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE})) {
      return `You do not have sufficient permissions to use Workflows, which is required to create a process development study. Please contact your administrator.`;
    }

    if (numErrors > 0) {
      return `Fix all errors in this ${procedureTypeNameSingular} to create a study.`;
    }

    if (isAutosaverPaused) {
      return `Submit changes to create a study.`;
    }

    if (accessLevel !== ProcedureAccessLevel.FULL_ACCESS) {
      // This probably means the procedure links to schemas that you don't have access to (e.g., for a material output configuration)
      return `You do not have sufficient access to this procedure to create a study. Please contact your administrator.`;
    }

    if (BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING) {
      if (!isViewingEffectiveVersion) {
        return `To create a study, please select an effective version of this ${procedureTypeNameSingular}.`;
      }
    }

    return null;
  }, [numErrors, isAutosaverPaused, accessLevel, procedureType, isViewingEffectiveVersion]);
}

function usePublishingRecordsForButton(procedureId: string): Array<PublishingRecordForButton> | null {
  const result = useBenchlingQuery<
    ProcedurePublishingRecordsForButton,
    ProcedurePublishingRecordsForButtonVariables
  >(PROCEDURE_PUBLISHING_RECORDS_FOR_BUTTON, {variables: {procedureId}});
  return result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.procedure)
    ? accessibleObjs(result.data.procedure.publishingRecords)
    : null;
}

interface ProcedureHeaderActionButtonsProps {
  locked: boolean;
  hasUnsavedChanges: boolean;
  // The autosaver is paused when the user is editing a tab in the method editor.
  isAutosaverPaused: boolean;
  // Will only be provided by ProcessFlowchartConfigEditorHeader for PoW procedures
  validationErrors?: Array<ProcessFlowchartValidationError>;
  flowchart?: StaticTemplateFlowchart;
  // flowchartConfig is passed by ProcessFlowchartConfigEditorHeader for PoW procedures.
  // Only the `id` field is needed here.
  flowchartConfig?: {id: string};
}

export default function ProcedureHeaderActionButtons(props: ProcedureHeaderActionButtonsProps): JSX.Element {
  const {classes} = useStyles();
  // This context is only populated in the procedure page for legacy procedures
  const {procedureValidationErrors: legacyProcedureValidationErrors} =
    ProcedureValidationContext.useContext();
  const {
    legacySaveImmediately,
    accessLevel,
    procedureMetadata,
    setProcedureMetadata,
    currentFlowchartVersionMetadata,
    hardRefresh,
    isProcedurePoW,
    duplicateProcedure,
    legacyMethodCidToMethodNameMap,
  } = ProcedurePageContext.useContext();
  assert(
    !isProcedurePoW || props.validationErrors != null,
    'Validation errors should be passed in for PoW procedures'
  );
  const numErrors = props.validationErrors?.length ?? legacyProcedureValidationErrors.length;

  // Build node CID to name map from flowchart for PoW error display
  const nodeCidToNodeNameMap = useMemo(() => {
    if (!props.flowchart) {
      return {};
    }
    const map: Record<string, string> = {};
    props.flowchart.nodesStaticData.forEach((nodeData) => {
      map[nodeData.id] = nodeData.nodeName;
    });
    return map;
  }, [props.flowchart]);

  const publishingRecordsForButton = usePublishingRecordsForButton(procedureMetadata.id);
  const isViewingEffectiveVersion =
    publishingRecordsForButton?.find(
      (record) => record.publishableVersionId === currentFlowchartVersionMetadata.id
    )?.publishingStatus === 'EFFECTIVE';

  const reasonPlanExperimentIsBlocked = useReasonPlanExperimentIsBlocked(
    numErrors,
    props.isAutosaverPaused,
    accessLevel,
    procedureMetadata.procedureType,
    isViewingEffectiveVersion
  );

  const {handleMove, handleArchive, handleUnarchive} = useProcedureActions({
    isProcedurePoW,
    procedureMetadata,
    setProcedureMetadata,
    legacySaveImmediately,
    procedureId: procedureMetadata.id,
    hardRefresh,
  });

  const enableCreateStudyButton =
    numErrors === 0 && !props.isAutosaverPaused && reasonPlanExperimentIsBlocked == null;

  const {
    movable: isMovable,
    archivable: isArchivable,
    unarchivable: isUnarchivable,
  } = procedureMetadata.permissions;

  const flushUnsavedChanges = async ({
    showLoadingScreen = true,
  }: {
    showLoadingScreen?: boolean;
  } = {}): Promise<void> => {
    if (props.locked) {
      return;
    }
    // We wait a tick, in case the user pressed the history panel button while a method name editor was open in the flowchart
    await sleep(0);
    // For PoW, the autosave manager is registered under the flowchart config ID, not the procedure ID.
    const autosaveItemId =
      isProcedurePoW && props.flowchartConfig != null ? props.flowchartConfig.id : procedureMetadata.id;
    if (showLoadingScreen) {
      await withLoadingScreen(bufferForUX(AutosaveManager.flushUnsavedChanges(autosaveItemId)), 'Saving...');
    } else {
      await bufferForUX(AutosaveManager.flushUnsavedChanges(autosaveItemId));
    }
  };

  const refreshAfterPublishingChange = (): void => {
    // This will trigger a full reload of all data (procedure, version, publishing records)
    hardRefresh();
    // Ensure we reset the URL, in case the user was creating a draft from an old version
    navigateToURL(procedurePageUrl(procedureMetadata.id), {skipTrigger: true});
  };

  const handleWithdraw = async (): Promise<void> => {
    // When withdrawing a published version, no need to pre-save, as the procedure is read only
    assert(props.locked, 'Withdrawing a procedure requires a published version, which should be read-only');
    assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing must be enabled');
    await withdrawProcedureVersion(procedureMetadata.id, currentFlowchartVersionMetadata.id);
    refreshAfterPublishingChange();
  };

  const handleCreateNewDraft = async (flowchartVersionApiIdentifier: string): Promise<void> => {
    assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing must be enabled');
    await createNewDraftProcedureVersion(procedureMetadata.id, flowchartVersionApiIdentifier);
    refreshAfterPublishingChange();
  };

  const handlePublish = async (versionName: string, versionDescription?: string): Promise<void> => {
    assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing must be enabled');
    assert(!props.locked, 'Procedure must not be read-only to publish');
    await flushUnsavedChanges();
    // For PoW, read the latest version ID from Redux, since autosave may have created
    // newer versions than what currentFlowchartVersionMetadata tracks from the initial page load.
    const versionIdToPublish =
      isProcedurePoW && props.flowchartConfig != null
        ? (getStore().getState().models.flowchartConfigs[props.flowchartConfig.id]?.flowchartConfigMetadata
            ?.currentVersionId ?? currentFlowchartVersionMetadata.id)
        : currentFlowchartVersionMetadata.id;
    await publishProcedureVersion(procedureMetadata.id, versionIdToPublish, versionName, versionDescription);
    refreshAfterPublishingChange();
  };

  return (
    <div
      className={classes.buttonGroup}
      {...getTestProps({component: 'ProcedureHeaderActionButtons', element: 'root'})}
    >
      {isProcedurePoW ? (
        <ProcedureErrorToolTip
          nodeCidToNodeNameMap={nodeCidToNodeNameMap}
          isProcedurePoW={true}
          processFlowchartValidationErrors={props.validationErrors!}
        />
      ) : (
        <ProcedureErrorToolTip nodeCidToNodeNameMap={legacyMethodCidToMethodNameMap} isProcedurePoW={false} />
      )}
      {BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING && (
        <>
          {publishingRecordsForButton && (
            <ProcedurePublishingButton
              doesProcedureHaveUnsavedChanges={props.hasUnsavedChanges}
              publishingRecords={publishingRecordsForButton}
              currentFlowchartVersionMetadata={currentFlowchartVersionMetadata}
              onPublish={async (versionName, versionDescription) => {
                await withLoadingScreen(handlePublish(versionName, versionDescription), 'Publishing...');
              }}
              onWithdraw={async () => {
                await withLoadingScreen(handleWithdraw(), 'Withdrawing...');
              }}
              onCreateDraft={async () => {
                await withLoadingScreen(
                  handleCreateNewDraft(currentFlowchartVersionMetadata.id),
                  'Creating draft...'
                );
              }}
              procedureType={procedureMetadata.procedureType}
              hasValidationErrors={numErrors > 0}
            />
          )}
          <ProcedureHistoryPanelButton
            beforeOpeningPanel={() => flushUnsavedChanges({showLoadingScreen: false})}
            onCreateDraft={async (flowchartVersionApiIdentifier: string) => {
              await withLoadingScreen(
                handleCreateNewDraft(flowchartVersionApiIdentifier),
                'Creating draft...'
              );
            }}
            doesProcedureHaveUnsavedChanges={props.hasUnsavedChanges}
          />
        </>
      )}
      <Popover
        placement={FloatingPlacement.TOP}
        renderContent={() => (
          <div className={classes.disablePlanExperimentPopover}>{reasonPlanExperimentIsBlocked}</div>
        )}
        isDisabled={reasonPlanExperimentIsBlocked == null}
      >
        <NewStudyDropdown
          actionOrigin={ActionOrigin.PROCEDURE_EXPERIMENT_DESIGNER}
          studyType={getStudyTypeForProcedureType(procedureMetadata.procedureType)}
          studyParams={{initialProcedureId: procedureMetadata.id}}
          buttonClassName={classes.button}
          buttonSize={ButtonSize.MEDIUM}
          buttonText="Create study"
          buttonDisabled={!enableCreateStudyButton}
          showCaret={true}
          waitForSave={flushUnsavedChanges}
        />
      </Popover>
      <OverflowMenu
        options={overflowMenuItemFactory({
          procedureMetadata,
          isMovable,
          isArchivable,
          isUnarchivable,
          isProcedureArchived: isArchived(procedureMetadata),
          hasUnsavedChanges: props.hasUnsavedChanges,
          isViewingLatestVersion: currentFlowchartVersionMetadata.isLatest,
          onMove: handleMove,
          onArchive: handleArchive,
          onUnarchive: handleUnarchive,
          onDuplicate: duplicateProcedure,
          locked: props.locked,
        })}
        menuProps={{dropLeft: true}}
        buttonProps={{
          size: ButtonSize.MEDIUM,
          dataTestAttrs: {component: 'ProcedureHeaderActionButtons', element: 'overflowMenu'},
        }}
      />
    </div>
  );
}

// @package Exported for testing
export function ProcedurePublishingButton(props: {
  publishingRecords: Array<PublishingRecordForButton>;
  currentFlowchartVersionMetadata: {id: string; createdAt: string; isLatest: boolean};
  onCreateDraft: () => void;
  onPublish: (versionName: string, versionDescription?: string) => void;
  onWithdraw: () => void;
  procedureType: ProcedureType;
  doesProcedureHaveUnsavedChanges: boolean;
  hasValidationErrors: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  assert(BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING, 'Publishing is not enabled for procedures');
  const {
    id: flowchartVersionId,
    createdAt: flowchartVersionCreatedAt,
    isLatest: flowchartIsLatestVersion,
  } = props.currentFlowchartVersionMetadata;

  const getPublishableInfoCallback = useCallback(
    (publishingRecords) =>
      getPublishableInfoForPublishingButton(
        publishingRecords,
        flowchartVersionId,
        flowchartVersionCreatedAt,
        flowchartIsLatestVersion
      ),
    [flowchartVersionId, flowchartVersionCreatedAt, flowchartIsLatestVersion]
  );

  return (
    <div className={classes.publishingButton}>
      <TemplatePublishingButton
        {...getPublishableInfoCallback(props.publishingRecords)}
        publishingRecords={props.publishingRecords}
        publishableModelTypeName={getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
          stringCase: StringCase.LOWER,
        })}
        onPublish={props.onPublish}
        onWithdraw={props.onWithdraw}
        onCreateDraft={props.onCreateDraft}
        isPublishOptionDisabled={props.doesProcedureHaveUnsavedChanges || props.hasValidationErrors}
        tooltipPlacement={FloatingPlacement.LEFT}
      />
    </div>
  );
}
